import type { Currency } from 'src/types/listing';
import type { ChainKey, ChainFamily } from 'src/blockchain';
import type { ApiChainToken, EscrowCreation, ApiAvailableChain } from 'src/types/chain';

import { DEFAULT_FAMILY, ENABLED_FAMILIES } from 'src/blockchain';

/**
 * Which network a *deal* settles on, and everything that follows from it (contracts v3, UI §2–§4).
 *
 * Buy Now picks a chain and then pays; an escrow deal is the other way round — api chose the chain
 * when the offer was created and wrote it into the row, because the money path (factory, token,
 * fee wallet, the address family of every participant) was resolved from it. So the screen does
 * not decide anything here: it reads `chainKey` off the deal and looks the rest up in the registry
 * api publishes at `GET /buyer/chains`.
 *
 * Three things depend on the answer and each one is wrong in a different way without it:
 *
 * - **the wallet.** One EVM address serves every EVM chain, so a connected wallet does not say
 *   which network a transaction would land on. Funding must switch the wallet to the deal's chain
 *   id first, or the buyer's stablecoin leaves on whatever network happened to be selected.
 * - **the invoice.** Amounts settle in the stablecoin of the listing's currency — EUR in EURC,
 *   USD in USDC on Base and USDT on TRON. A hard-coded "USDT" label lies on three of those four.
 * - **the tracker.** Every on-chain step links to its transaction, and the explorer prefix is a
 *   property of the chain (Basescan for Base, Tronscan for TRON), published by api.
 */

/** A deal's network, resolved against the api registry. */
export interface DealChain {
  chainKey: ChainKey;
  family: ChainFamily;
  displayName: string;
  /** EVM chain id to switch the wallet to before signing; absent on TRON. */
  chainId?: number;
  /** The stablecoin this deal settles in — null when the currency (or the token for it) is unknown. */
  token: ApiChainToken | null;
  /** Explorer URL prefix a transaction hash is appended to. */
  explorerTxUrl: string;
  /** False when this build carries no wallet stack for the family — nothing here can be signed. */
  signable: boolean;
  /** Who creates the escrow contract here (api#380); absent against an api older than the field. */
  escrowCreation?: EscrowCreation;
}

interface ResolveParams {
  /** `deal.chainKey`. Absent against an api older than the field, or on a row written before it. */
  chainKey?: ChainKey;
  /** `GET /buyer/chains`; `undefined` while it is still loading. */
  buyerChains: ApiAvailableChain[] | undefined;
  /** Fiat currency of the deal's listing; decides the stablecoin. Absent when the listing is gone. */
  listingCurrency?: Currency;
  /** Families whose wallet stack this build carries. A parameter only so tests need no rebuild. */
  buildFamilies?: ChainFamily[];
  /**
   * Family assumed for a deal that names no chain. Same reason as `buildFamilies`: both default to
   * build constants, and a test that depended on them would be testing this deployment's `.env`
   * ([[tests-must-not-read-dotenv]]).
   */
  defaultFamily?: ChainFamily;
}

/**
 * The deal's chain, or `null` while the registry has not answered yet / does not know the chain.
 *
 * A `null` is never a reason to guess a default and sign against it: the caller shows the deal
 * read-only until the answer arrives. The one guess that *is* made is the legacy one — a deal
 * with no `chainKey` at all predates the multichain track, and every such row settled on the
 * default family's single chain, which is what api itself falls back to (`chainKey ?? TRON`).
 */
export function resolveDealChain(params: ResolveParams): DealChain | null {
  const {
    chainKey,
    buyerChains,
    listingCurrency,
    buildFamilies = ENABLED_FAMILIES,
    defaultFamily = DEFAULT_FAMILY,
  } = params;

  if (!buyerChains?.length) {
    return null;
  }

  const entry = chainKey
    ? buyerChains.find((chain) => chain.chainKey === chainKey)
    : singleChainOfFamily(buyerChains, defaultFamily);

  if (!entry) {
    return null;
  }

  return {
    chainKey: entry.chainKey,
    family: entry.family,
    displayName: entry.displayName,
    chainId: entry.chainId,
    token: resolveToken(entry, listingCurrency),
    explorerTxUrl: entry.explorerTxUrl,
    signable: buildFamilies.includes(entry.family),
    escrowCreation: entry.escrowCreation,
  };
}

/**
 * The legacy fallback: the one enabled chain of the default family.
 *
 * Refuses to answer when that family spans several chains — an api old enough to omit `chainKey`
 * never had more than one, so "several" means the field is missing for some other reason, and
 * picking one of them would be inventing where the deal settled.
 */
function singleChainOfFamily(chains: ApiAvailableChain[], family: ChainFamily): ApiAvailableChain | undefined {
  const ofFamily = chains.filter((chain) => chain.family === family);

  return ofFamily.length === 1 ? ofFamily[0] : undefined;
}

/**
 * The token the deal settles in: the one pegged to the listing's currency, and otherwise the only
 * one the chain has.
 *
 * The fallback covers two cases, and both are about naming the money that actually moves rather
 * than the money that ought to:
 *
 * - **the currency is unknown** — the listing snapshot is null because the listing is gone;
 * - **the chain carries no token for that currency.** On TRON that is every EUR listing: the
 *   registry says a euro car "can never settle here", but the TRON escrow service pins the shared
 *   Nile USDT whatever the listing is priced in, so a euro deal made today really is paid in USDT.
 *   Printing "6,000" with no ticker would hide the one fact the buyer needs. This disappears with
 *   PR-11, which stops new escrow deals on TRON altogether.
 *
 * A chain with several tokens and no match answers nothing: there the guess would be a coin flip.
 */
function resolveToken(entry: ApiAvailableChain, listingCurrency?: Currency): ApiChainToken | null {
  if (listingCurrency && entry.tokens[listingCurrency]) {
    return entry.tokens[listingCurrency] as ApiChainToken;
  }

  const tokens = Object.values(entry.tokens).filter((token): token is ApiChainToken => !!token);

  return tokens.length === 1 ? tokens[0] : null;
}

/** Explorer link for one transaction of this deal. Empty when there is no hash to link. */
export const dealTxLink = (chain: DealChain | null, txHash: string | null | undefined): string | null =>
  chain && txHash ? `${chain.explorerTxUrl}${txHash}` : null;

/** Short form of a hash for a link label: `0x1234…cdef`. */
export const shortenHash = (txHash: string): string =>
  txHash.length <= 14 ? txHash : `${txHash.slice(0, 8)}…${txHash.slice(-6)}`;
